import React, { useEffect, useMemo, useState } from "react";
import WidgetCard from "@/components/controls/WidgetCard";
import { useTranslation } from "react-i18next";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import { UserData } from "@/entities/users/types";
import { LeadAnalysisData, SettingsPeriod } from "@/entities/reporting/types";
import { periodsDropdownProps } from "@/components/layouts/ConversionRates/helpers";
import {
  periodEnumToString,
  periodStringToEnum
} from "@/entities/reporting/helpers";
import MetricsDashboard from "@/components/layouts/LeadAnalysis/MetricsDashboard";
import Text from "@/components/ui/text";
import ComparisonChart, {
  ComparisonChartProps
} from "@/components/graphs/ComparisonChart/ComparisonChart";
import { Switch } from "@/components/ui/switch";
import LineChart from "@/components/graphs/WinRate/LineChart";
import BarChart from "@/components/graphs/WinRate/BarChart";
import LeadAnalysisDirectReportModal from "@/components/layouts/LeadAnalysis/LeadAnalysisDirectReportModal";
import { LeadAnalysisBreadcrumbsProvider } from "@/components/layouts/LeadAnalysis/LeadAnalysisBreadcrumbsProvider";
import {
  extractPeerComparison,
  useAllLeadChartData,
  useLeadSourceChartData,
  getKpiTitle
} from "@/components/layouts/LeadAnalysis/helpers";
import DreamerStackBarChart from "@/components/DreamerCharts/DreamerStackBarChart";
import DreamerMultiLineChart from "@/components/DreamerCharts/DreamerMultiLineChart";
import {
  LeadAnalysisKPI,
  PeerComparisonResult
} from "@/components/layouts/LeadAnalysis/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { reducePrecision } from "@/utils/numbers";

export type LeadAnalysisPropsType = {
  data?: LeadAnalysisData;
  userData?: UserData;
  period: SettingsPeriod;
  periodSelect: (period: SettingsPeriod) => void;
  isLoading?: boolean;
  isAE: boolean;
  drillDownHidden?: boolean;
};

const LeadAnalysis: React.FC<LeadAnalysisPropsType> = ({
  userData,
  period,
  periodSelect,
  isLoading,
  data,
  isAE,
  drillDownHidden = false
}) => {
  const { t } = useTranslation();
  const showIndustryValue = useFeatureFlag(FeatureFlags.INDUSTRY_VALUE).enabled;

  const [showComparison, setShowComparison] = useState(false);
  const [splitBySource, setSplitBySource] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState<LeadAnalysisKPI>(
    LeadAnalysisKPI.avgMonthlyQualifiedConvertedLeads
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [peerComparison, setPeerComparison] = useState<PeerComparisonResult>();

  const mergedComparisonChartProps = useMemo(() => {
    if (peerComparison) {
      return {
        datasets: [
          {
            name: t(`comparison.titles.you`),
            value: reducePrecision(Number(peerComparison.you?.value), 1)
          },
          {
            name: t(`comparison.titles.team`),
            value: reducePrecision(Number(peerComparison.team.value), 1)
          },
          {
            name: t(`comparison.titles.organization`),
            value: reducePrecision(Number(peerComparison.org.value), 1)
          },
          {
            name: t(`comparison.titles.industry`),
            value: peerComparison.industry
              ? reducePrecision(Number(peerComparison.industry.value), 1)
              : 0
          }
        ],
        userData
      } as ComparisonChartProps;
    }
  }, [peerComparison]);

  const {
    avgMonthlyNewLeadsByMonth,
    leadConversionRateByMonth,
    leadQualificationRateByMonth
  } = useAllLeadChartData(data);

  const leadSourceChart = useLeadSourceChartData(
    data,
    selectedKpi,
    t("leadAnalysis.noSource")
  );
  const isAvgLeadsKpi =
    selectedKpi === LeadAnalysisKPI.avgMonthlyQualifiedConvertedLeads;
  const canSplitBySource = leadSourceChart.segments.length > 0;
  const showSourceSplit = splitBySource && canSplitBySource;

  useEffect(() => {
    if (data) {
      const newPeerComparison = extractPeerComparison(data, selectedKpi);
      if (newPeerComparison) {
        setPeerComparison(newPeerComparison);
      }
    }
  }, [selectedKpi, data]);

  if (!data) return null;

  return (
    <>
      <WidgetCard
        cardHeight="32rem"
        title={t("leadAnalysis.title")}
        isLoading={isLoading}
        filter={
          <Dropdown
            {...periodsDropdownProps(t)}
            label={t("conversionRates.timeframe")}
            defaultValue={periodEnumToString(period)}
            valueSelected={(value) => periodSelect(periodStringToEnum(value))}
          />
        }
        showFeedBackButton
        infoTooltip={t("leadAnalysis.info")}
        isDrillDownHidden={isAE || drillDownHidden}
        drillDownOnClick={() => setIsModalOpen(!isModalOpen)}
        feedBackButtonIdContext={"lead-analysis"}
      >
        <div className="flex flex-col w-full gap-8">
          <div className="flex flex-row">
            <MetricsDashboard
              selectedKpi={selectedKpi}
              setSelectedKpi={setSelectedKpi}
              data={data as LeadAnalysisData}
              period={period}
            />
          </div>
          <div className="flex flex-row h-full gap-8 w-full">
            <div className="w-full">
              <div className="flex flex-col gap-6">
                <div className="flex flex-row justify-between">
                  <Text variant="label">
                    {getKpiTitle(selectedKpi, t)}{" "}
                    {t(`leadAnalysis.time.byMonth`)}
                  </Text>
                  <Text as="div" variant="bodySecondary" className="flex gap-6">
                    {canSplitBySource && (
                      <div className="flex gap-2 items-center">
                        <span>{t("leadAnalysis.splitBySource")}</span>
                        <Switch
                          aria-label={t("leadAnalysis.splitBySource")}
                          checked={splitBySource}
                          onCheckedChange={(checked) => {
                            setSplitBySource(checked);
                            if (checked) setShowComparison(false);
                          }}
                        />
                      </div>
                    )}
                    <div className="flex gap-2 items-center">
                      <span>{t("winRate.comparePeers")}</span>
                      <Switch
                        checked={showComparison}
                        onCheckedChange={(checked) => {
                          setShowComparison(checked);
                          if (checked) setSplitBySource(false);
                        }}
                      />
                    </div>
                  </Text>
                </div>
                <div className="h-full w-full">
                  {showSourceSplit && isAvgLeadsKpi ? (
                    <DreamerStackBarChart
                      height={200}
                      chartData={leadSourceChart.chartData}
                      segments={leadSourceChart.segments}
                      tooltipYpos={20}
                      isPercentage={false}
                    />
                  ) : showSourceSplit ? (
                    <DreamerMultiLineChart
                      height={200}
                      chartData={leadSourceChart.chartData}
                      lines={leadSourceChart.segments}
                      isPercentage
                    />
                  ) : isAvgLeadsKpi ? (
                    <BarChart
                      height={200}
                      chartData={avgMonthlyNewLeadsByMonth}
                      showComparison={showComparison}
                      userData={userData}
                      tooltipYpos={20}
                      chartWidgetType="leadAnalysis"
                      isPercentage={false}
                      hideIndustry={!showIndustryValue}
                    />
                  ) : (
                    <LineChart
                      height={200}
                      chartData={
                        selectedKpi === LeadAnalysisKPI.LeadConversionRate
                          ? leadConversionRateByMonth
                          : leadQualificationRateByMonth
                      }
                      showComparison={showComparison}
                      userData={userData}
                      chartWidgetType="winRate"
                    />
                  )}
                </div>
              </div>
            </div>
            {mergedComparisonChartProps && (
              <div className="flex flex-col justify-end h-60 w-[12.5rem]">
                <Text as="div" variant="label" className="text-start">
                  {getKpiTitle(selectedKpi, t, true)}
                </Text>
                <Text as="div" variant="bodySecondary" className="text-start">
                  {t("comparison.title")}
                </Text>
                <ComparisonChart
                  {...mergedComparisonChartProps}
                  hideIndustry={!showIndustryValue}
                  showCurrencySign={false}
                  unit={
                    selectedKpi ===
                    LeadAnalysisKPI.avgMonthlyQualifiedConvertedLeads
                      ? ""
                      : "%"
                  }
                />
              </div>
            )}
          </div>
        </div>
      </WidgetCard>
      <LeadAnalysisBreadcrumbsProvider
        leadUser={userData}
        period={period}
        enableDirectReportsData={isModalOpen}
      >
        <LeadAnalysisDirectReportModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          period={period}
          periodSelect={periodSelect}
        />
      </LeadAnalysisBreadcrumbsProvider>
    </>
  );
};

export default LeadAnalysis;
